import { axiosApi } from '@/axios'
import { Body, Iterable, Paginator, Params } from '@/utils/interfaces'
import { AxiosResponse } from 'axios'
import {
  AgencyTopUpPrice,
  CreateAgentConfigDTO,
  CreateTopUpClientDTO,
  CreateTopUpDTO,
  GetAgentCreditDTO,
  GetProductDTO,
  GetProductProviderCodeDTO,
  GetProviderDTO,
  GetTopUpPriceDTO,
  ProviderConfig,
  ProviderCredit,
  Report,
  TopUp,
  TopUpAgent,
  TopUpBalanceTransaction,
  TopUpProviderCreditTransaction,
  TopUpAgencyTransaction,
  TopUpClient,
  TopUpCreditTransaction,
  TopUpDeal,
  TopUpPlatformPrice,
  TopUpPrice,
  TopupRequests,
  TransferTopUpCredit,
  UpdateProviderCreditDTO,
  UpdateSharedProviderCreditDTO,
  GetTopUpDTO,
} from '@/utils/interfaces/topup.interface'
import { createOrUpdate, createOrUpdateJSON } from '@/utils/functions'
import { ApiController } from '@/utils/classes/ApiController'
const API_URL = '/topups/'

export const createTopUp = (
  body: CreateTopUpDTO,
): Promise<AxiosResponse<TopupRequests>> =>
  axiosApi.post(API_URL + 'list/', body)

export const createGenericTopUp = (body: CreateTopUpDTO) =>
  axiosApi.post<TopupRequests>(API_URL + 'create-generic/', body)

export const getTopUps = (
  params: Params,
): Promise<AxiosResponse<Paginator<TopUp>>> =>
  axiosApi.get(API_URL + 'list/', { params })

export const getTopUpAgents = (
  params?: Params,
): Promise<AxiosResponse<Paginator<TopUpAgent>>> =>
  axiosApi.get(API_URL + 'agents/', { params })

export const getTopUpAgentById = (
  uuid: string,
): Promise<AxiosResponse<TopUpAgent>> =>
  axiosApi.get(API_URL + 'agents/' + uuid + '/')

export const createAgentConfiguration = (
  body: CreateAgentConfigDTO,
): Promise<AxiosResponse<TopUpPrice>> =>
  axiosApi.post(API_URL + 'all-agent-prices/', body)

export const getAgentPrices = (
  params?: Params,
): Promise<AxiosResponse<Paginator<GetTopUpPriceDTO>>> =>
  axiosApi.get(API_URL + 'all-agent-prices/', { params })

export const getAgentPriceById = (
  uuid: string,
): Promise<AxiosResponse<GetTopUpPriceDTO>> =>
  axiosApi.get(`${API_URL}agent-prices/${uuid}/`)

export const getAgencyPrices = (
  params?: Params,
): Promise<AxiosResponse<Paginator<AgencyTopUpPrice>>> =>
  axiosApi.get(API_URL + 'agency-prices/', { params })

export const getSharedProducts = (
  params?: Params,
): Promise<AxiosResponse<Paginator<AgencyTopUpPrice>>> =>
  axiosApi.get(API_URL + 'shared-products/', { params })

export const getAgencyPriceById = (
  uuid: string,
): Promise<AxiosResponse<AgencyTopUpPrice>> =>
  axiosApi.get(`${API_URL}agency-prices/${uuid}/`)

export const getSharedProductById = (
  uuid: string,
): Promise<AxiosResponse<AgencyTopUpPrice>> =>
  axiosApi.get(`${API_URL}shared-products/${uuid}/`)

export const updateAgentPrices = (
  body: Iterable,
): Promise<AxiosResponse<Paginator<TopUpPrice>>> =>
  axiosApi.patch(`${API_URL}agent-prices/${body.uuid}/`, body)

export const updateAgencyPrices = (
  body: Iterable,
): Promise<AxiosResponse<Paginator<TopUpPrice>>> =>
  axiosApi.patch(`${API_URL}agency-prices/${body.uuid}/`, body)

export const createAgencyPrices = (
  body: Iterable,
): Promise<AxiosResponse<TopUpPrice>> =>
  axiosApi.post(`${API_URL}agency-prices/`, body)

export const getProviders = (
  params?: Params,
): Promise<AxiosResponse<Paginator<GetProviderDTO>>> =>
  axiosApi.get(API_URL + 'providers/', { params })

export const getSharedProviders = (
  params?: Params,
): Promise<AxiosResponse<Paginator<GetProviderDTO>>> =>
  axiosApi.get(API_URL + 'shared-providers/', { params })

export const createOrUpdateAgencyPrice = (
  body: Body,
): Promise<AxiosResponse<AgencyTopUpPrice>> =>
  createOrUpdateJSON<AgencyTopUpPrice>(API_URL + 'agency-prices/', body)

export const createOrUpdateSharedProduct = (
  body: Body,
): Promise<AxiosResponse<AgencyTopUpPrice>> =>
  createOrUpdateJSON<AgencyTopUpPrice>(API_URL + 'shared-products/', body)

export const getProducts = (
  params?: Params,
): Promise<AxiosResponse<Paginator<GetProductDTO>>> =>
  axiosApi.get(API_URL + 'products/', { params })

export const createOrUpdateProduct = (
  body: Body,
): Promise<AxiosResponse<GetProductDTO>> =>
  createOrUpdateJSON(API_URL + 'products/', body)

export const getProductProviderCodes = (
  params?: Params,
): Promise<AxiosResponse<Paginator<GetProductProviderCodeDTO>>> =>
  axiosApi.get(API_URL + 'product-provider-codes/', { params })

export const createOrUpdateProductProviderCode = (
  body: Body,
): Promise<AxiosResponse<GetProductProviderCodeDTO>> =>
  createOrUpdateJSON(API_URL + 'product-provider-codes/', body)

export const deleteProductProviderCode = (
  uuid: string,
): Promise<AxiosResponse<void>> =>
  axiosApi.delete(`${API_URL}product-provider-codes/${uuid}/`)

export const createOrUpdateProvider = (
  body: Body,
): Promise<AxiosResponse<GetProductDTO>> =>
  createOrUpdateJSON(API_URL + 'providers/', body)

export const createOrUpdateSharedProvider = (
  body: Body,
): Promise<AxiosResponse<GetProductDTO>> =>
  createOrUpdateJSON(API_URL + 'shared-providers/', body)

export const getAgentsCredit = (
  params?: Params,
): Promise<AxiosResponse<Paginator<GetAgentCreditDTO>>> =>
  axiosApi.get(API_URL + 'agent-credits/', { params })

export const createAgentCredit = async (
  body: TopUpAgent,
): Promise<TopUpAgent> => {
  let response
  if (body.topup_credit_uuid) {
    response = await axiosApi.patch<GetAgentCreditDTO>(
      `${API_URL}agent-credits/${body.topup_credit_uuid}/`,
      {
        credit: body.topup_credit,
        description: body.description,
      },
    )
  } else {
    response = await axiosApi.post<GetAgentCreditDTO>(
      `${API_URL}agent-credits/`,
      {
        agent: body.uuid,
        credit: body.topup_credit,
        description: body.description,
      },
    )
  }
  body.topup_credit = response.data.credit
  body.topup_credit_uuid = response.data.uuid
  return body
}

export const transferTopUpCredit = (
  body: TransferTopUpCredit,
): Promise<AxiosResponse<TransferTopUpCredit>> =>
  axiosApi.post(API_URL + 'credit-transfer/', body)

export const getProviderCredit = (
  uuid: string,
  params?: Params,
): Promise<AxiosResponse<ProviderCredit>> =>
  axiosApi.get(`${API_URL}provider-real-credit/${uuid}/`, { params })

export const generateReport = (params?: Params): Promise<AxiosResponse<any>> =>
  axiosApi.get(API_URL + 'xls-export/', {
    params,
    responseType: 'arraybuffer',
  })

export const updateProviderConfig = (
  uuid: string,
  body: Body,
  provider: string,
): Promise<AxiosResponse<ProviderCredit>> =>
  axiosApi.patch(`${API_URL}provider-config/${uuid}/`, body, {
    params: {
      provider__uuid: provider,
    },
  })

export const createProviderConfig = (
  body: {
    agency: string
  },
  provider: string,
): Promise<AxiosResponse<ProviderConfig>> =>
  axiosApi.post(API_URL + 'provider-config/', body, {
    params: {
      provider__uuid: provider,
    },
  })

export const getProviderConfig = (
  agency: string,
  provider: string,
): Promise<AxiosResponse<ProviderConfig>> =>
  axiosApi.get(`${API_URL}provider-config/${agency}/`, {
    params: {
      provider__uuid: provider,
    },
  })

export const deleteProviderConfig = (
  agency: string,
  provider: string,
): Promise<AxiosResponse<ProviderConfig>> =>
  axiosApi.delete(`${API_URL}provider-config/${agency}/`, {
    params: {
      provider__uuid: provider,
    },
  })

export const updateProviderCredit = (
  body: UpdateProviderCreditDTO,
): Promise<AxiosResponse<UpdateProviderCreditDTO>> =>
  axiosApi.post(API_URL + 'provider-update-credit/', body)

export const updateSharedProviderCredit = (
  body: UpdateSharedProviderCreditDTO,
): Promise<AxiosResponse<UpdateProviderCreditDTO>> =>
  axiosApi.post(API_URL + 'shared-provider-update-credit/', body)

export const createOrUpdateTopUpDeal = (
  body: TopUpDeal,
): Promise<AxiosResponse<TopUpDeal>> =>
  createOrUpdate(API_URL + 'deals/', body, 'image')

export const getTopUpDeals = (
  params?: Params,
): Promise<AxiosResponse<Paginator<TopUpDeal>>> =>
  axiosApi.get(API_URL + 'deals/', { params })

export const getReport = (params?: Params): Promise<AxiosResponse<Report>> =>
  axiosApi.get(API_URL + 'report/', { params })

export const recheckTopUp = (uuid: string): Promise<AxiosResponse<any>> =>
  axiosApi.post(API_URL + 're-check/', { topup: uuid })

export const resendTopUp = (uuid: string): Promise<AxiosResponse<any>> =>
  axiosApi.post(API_URL + 're-send/', { topup: uuid })

export const cancelTopUp = (uuid: string): Promise<AxiosResponse<any>> =>
  axiosApi.post(API_URL + 'cancel/', { topup: uuid })

export const releaseTopUp = (uuid: string): Promise<AxiosResponse<any>> =>
  axiosApi.post(API_URL + 'send-hold/', { topup: uuid })

export const revokeTopUp = (uuid: string): Promise<AxiosResponse<any>> =>
  axiosApi.post(API_URL + 'revoke/', { topup: uuid })

export const retryTopUp = (uuid: string): Promise<AxiosResponse<any>> =>
  axiosApi.post(API_URL + 'retry/', { topup: uuid })

export const nuclearLaunch = (amount: number): Promise<AxiosResponse<any>> =>
  axiosApi.post(API_URL + 'send-pre-topups/', { amount_to_send: amount })

export const createTopUpPlatformPrice = (
  body: Body,
): Promise<AxiosResponse<TopUpPlatformPrice>> =>
  createOrUpdateJSON('/topups/prices/', body)

export const getTopUpPlatformPrices = (
  params?: Params,
): Promise<AxiosResponse<Paginator<TopUpPlatformPrice>>> =>
  axiosApi.get('/topups/prices/', { params })

export const getPreTopupStatus = () =>
  axiosApi.get<{ status: boolean }>('/topups/pre-topup-status/')

export const topupClientController = new ApiController<
  TopUpClient,
  CreateTopUpClientDTO
>('/topups/clients/')

export const topupCreditTransactionsController = new ApiController<
  TopUpCreditTransaction,
  any
>('/topups/transactions/credit/')

export const topupBalanceTransactionsController = new ApiController<
  TopUpBalanceTransaction,
  any
>('/topups/transactions/balance/')

export const topupProviderCreditTransactionsController = new ApiController<
  TopUpProviderCreditTransaction,
  any
>('/topups/transactions/provider-credit/')

export const topupAgencyTransactionsController = new ApiController<
  TopUpAgencyTransaction,
  any
>('/topups/billtransactions/')

export const generateTopUpInvoice = (params?: Params) =>
  axiosApi.get('/topups/invoice/', { params })

export const togglePreTopup = (body: { is_active: boolean }) =>
  axiosApi.post<{ is_active: boolean }>(
    '/topups/change-pre-topup-status/',
    body,
  )

export const getBalanceTopUp = (params?: Params) =>
  axiosApi.get('/topups/balance-topups/', { params })

export const toggleBalanceTrasnferStatus = (body: { is_active: boolean }) =>
  axiosApi.post<{ is_active: boolean }>(
    '/topups/change-balance-transfer-status/',
    body,
  )

export const getBalanceTransferStatus = () =>
  axiosApi.get<{ is_active: boolean }>(
    '/topups/change-balance-transfer-status/',
  )

export const assignTransferPopUp = (topupUuid: string, assigned_to: string) =>
  axiosApi.patch('/topups/assign-balance-topup/' + topupUuid + '/', {
    assigned_to,
  })

export const confirmTopUp = (uuid: string, status: string, files: File[]) => {
  const data = new FormData()

  for (const file of files) {
    data.append('confirmation_files', file)
  }

  data.append('status', status)

  return axiosApi.patch(`/topups/check-balance-topup/${uuid}/`, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}
